import type { RouteMeta, RouteRecordRaw } from 'vue-router';
import { type FunctionKey } from '@/types';
import { type LayoutVariant } from '@/utils/composables/useLayoutVariant';

/**
 * 画面を開くために必要な条件を表す。
 * `public`: 認証不要。`authenticated`: 要ログインで、利用可否の判定は不要。
 * それ以外はFunctionKeyで、`available_function_keys`に含まれるかで利用可否を判定する。
 */
export type AccessScope = 'public' | 'authenticated' | FunctionKey;

/**
 * 左メニュー（rail のハイライトとパネルの中身）のセクション。
 * セクションを新設する手順は README「ルーティングと左メニュー」を参照。
 */
export const MENU_SECTIONS = [
  'news',
  'my_page',
  'group',
  'survey',
  'others', // 旧デザインのみ
  'notifications', // 新デザインのみ
  'search',
  'admin',
  'settings', // 新デザインのみ
] as const;

export type MenuSection = (typeof MENU_SECTIONS)[number];

declare module 'vue-router' {
  interface RouteMeta {
    /** ページタイトル。document.title に使う（pcRouter の afterEach） */
    title?: string;
    /**
     * 左メニューのセクション。matched チェーンでマージされるため、
     * withMenuSection でグループに指定すれば children 側の指定は不要。
     * null は「意図的に左メニューを出さない」を表す（未設定と区別するため）。
     */
    menuSection?: MenuSection | null;
    /**
     * 画面種別。認証ガードと個別画面の分岐に使う。左メニューには使わない
     * （左メニューは menuSection に一本化されている）。
     *
     * 有効な値は以下だけ。3つの用途が1つのフィールドに混ざっているので、
     * 値を増やすときは読み手も一緒に足す。
     * - 認証ガード: account / mobile_app_download / no_login_required_redirect / user
     * - 旧レイアウトの画面判定: search / mypage / userSettings / notifications / user / group / theme
     * - エージェントのレール選択状態: techExploration / patentFtoProject / patentFtoAnalysis
     * - 特許調査のパネル切り替え: patentFtoSettings
     *
     * main-view-next.vue が唯一のレイアウトになったら、旧レイアウトの画面判定に使っている値は
     * 読み手（main-view.vue / menu/left-menu.vue）ごと削除できる。
     * 認証ガードの値は requiresAuth のような専用フィールドに置き換えるのが望ましく、
     * そこまで整理すれば残りを文字列リテラルのunionにできる。
     */
    type?: string;
    /** meta description。アカウント関連ページのみ（pc-app.vue が読む） */
    desc?: string;
    /** true のとき遷移時のスクロール位置リセットを抑止する（scrollBehavior） */
    preserveScroll?: boolean;
    /**
     * その画面が片方のデザインにしか存在しないことを表す。
     * 反対のデザインで開かれたときはトップに戻す（redirectToCurrentDesign）。
     * 旧/新で別ルートになっている画面は designRoutes の対応表で対向に飛ばすので、指定しない。
     */
    availableIn?: LayoutVariant;
    /**
     * ルーターガード（pcRouter.ts）が利用可否を判定するためのスコープ。
     * matched チェーンでマージされるため、
     * withMenuSection / withoutMenuSection の第3引数で渡せばグループ内の子ルートに一括で継承させられる。
     * グループ内でスコープが混在する場合（admin / my_page）は各ルート自身の meta に書く。
     */
    accessScope?: AccessScope;
  }
}

declare const menuSectionDeclared: unique symbol;

/**
 * withMenuSection / withoutMenuSection の戻り値だけが満たす型。
 * シェル配下のルートをこの型で受けることで、セクションの指定漏れを型エラーにする。
 */
export type ShellRouteGroup = {
  /** ヘルパー経由で作られたことを示すマーカー（実行時には存在しない） */
  readonly [menuSectionDeclared]: true;
  path: string;
  meta: RouteMeta;
  children: RouteRecordRaw[];
};

/**
 * 指定セクションの左メニューを表示するページ群。
 * accessScopeはグループ内が単一スコープのときだけ渡す。混在する場合（admin / my_page）は
 * 渡さずに、各ルート自身の meta.accessScope に書く。
 */
export const withMenuSection = (
  menuSection: MenuSection,
  children: RouteRecordRaw[],
  accessScope?: AccessScope,
): ShellRouteGroup =>
  ({
    path: '',
    meta: { menuSection, ...(accessScope && { accessScope }) },
    children,
  }) as ShellRouteGroup;

/** 左メニューを表示しないページ群（お知らせ・他メンバーページなど） */
export const withoutMenuSection = (
  children: RouteRecordRaw[],
  accessScope?: AccessScope,
): ShellRouteGroup =>
  ({
    path: '',
    meta: { menuSection: null, ...(accessScope && { accessScope }) },
    children,
  }) as ShellRouteGroup;
